import { ArrowBackIcon, NotAllowedIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Link as ChakraLink,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  Input,
  Spacer,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useRef } from "react";
import { BiGitRepoForked } from "react-icons/bi";
import { BsArchive, BsShare } from "react-icons/bs";
import { MdErrorOutline } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation } from "wouter";

import { Pagination } from "@nens/chakra/components";
import { BulkExportType, BulkFileConfig, CompositeMapConfig, RorConfig } from "@nens/ldo/api-v1";
import {
  Breadcrumbs,
  BulkExportDownload,
  BulkExportRastersMap,
  BulkExportTypeDescription,
  EmptyExportListItem,
  ErrorBox,
  ExportBulkFileFields,
  ExportCompositeMapsFields,
  ExportListItem,
  ExportRorFields,
} from "@nens/ldo/components";

import { useBulkExportHook } from "../bulk-exports-hook";
import { DeleteExportButton } from "../delete-export-button";
import { ExportStatus } from "../export-status/export-status";
import { getBulkExportCloneUrl, getBulkExportListUrl, getBulkExportUrl } from "../routes";

interface BulkExportDetailProps {
  id: string;
  exportType: BulkExportType;
}

export function BulkExportDetail({ id, exportType }: BulkExportDetailProps) {
  const intl = useIntl();
  const setLocation = useLocation()[1];
  const addScenarioInputRef = useRef<HTMLInputElement>(null);

  const {
    actionAllowed,
    addScenarioId,
    removeScenarioId,
    bulkExportResponse,
    exportUpdating,
    scenarioIds,
    scenarioResponses,
    changeExportError,
    submitExport,
    archive,
    unarchive,
    updateConfig,
    share,
    bulkExport,
    editAllowed,
    canStart,
    canStartLoaded,
    formErrorKeys,
    pagination,
    numPages,
  } = useBulkExportHook(exportType, Number(id), { loadScenarios: true });

  const listUrl = getBulkExportListUrl(exportType);
  const exportUrl = getBulkExportUrl(exportType, Number(id));
  const cloneUrl = getBulkExportCloneUrl(exportType, Number(id));

  if (
    bulkExportResponse.isError ||
    (bulkExport && (!actionAllowed("view") || bulkExport.type !== exportType))
  ) {
    setLocation(listUrl);
    return null;
  }

  const errors = (bulkExport?.errors ?? []).map((error) => error.msg);

  const addScenario = () => {
    if (addScenarioInputRef.current) {
      const scenarioId = parseInt(addScenarioInputRef.current.value);
      if (!isNaN(scenarioId)) {
        addScenarioInputRef.current.value = "";
        addScenarioId(scenarioId);
      }
    }
  };

  return (
    <Box w="100%">
      <Breadcrumbs
        crumbs={[
          { label: intl.formatMessage({ id: "pages.home", defaultMessage: "Home" }), href: "/" },
          {
            label: intl.formatMessage({
              id: "pages.export_scenarios",
              defaultMessage: "Export Scenarios",
            }),
            href: listUrl,
          },
          {
            label: bulkExport?.name ?? "",
            href: exportUrl,
          },
        ]}
      />
      <Container maxW={"7xl"} py={"24px"} justifyItems="center" w="100%">
        <Heading mb={4}>
          <BulkExportTypeDescription exportType={exportType} />
        </Heading>
        <Flex justifyContent="space-between">
          <ButtonGroup variant="outline">
            <Button
              onClick={() => bulkExport !== null && setLocation(listUrl)}
              title={intl.formatMessage({
                id: "pages.export_scenarios.back",
                defaultMessage: "Back to exports",
              })}
              leftIcon={<ArrowBackIcon />}
            >
              <FormattedMessage id="pages.export_scenarios.back" defaultMessage="Back to exports" />
            </Button>
          </ButtonGroup>
          <ButtonGroup variant="outline">
            {actionAllowed("clone") ? (
              <Button
                variant="outline"
                mx="1"
                title={intl.formatMessage({
                  id: "export.clone.description",
                  defaultMessage: "Clone this export task",
                })}
                leftIcon={<BiGitRepoForked />}
                onClick={() => setLocation(cloneUrl)}
              >
                <Text
                  mr="1"
                  display={{
                    base: "none",
                    md: "block",
                  }}
                >
                  <FormattedMessage id="export.clone" defaultMessage="Clone" />
                </Text>
              </Button>
            ) : null}
            {actionAllowed("archive") ? (
              <Button
                title={intl.formatMessage({ id: "buttons.archive", defaultMessage: "Archive" })}
                leftIcon={<BsArchive />}
                onClick={archive}
              >
                {intl.formatMessage({ id: "buttons.archive", defaultMessage: "Archive" })}
              </Button>
            ) : null}
            {actionAllowed("unarchive") ? (
              <Button
                title={intl.formatMessage({ id: "buttons.unarchive", defaultMessage: "Unarchive" })}
                leftIcon={<BsArchive />}
                onClick={unarchive}
              >
                {intl.formatMessage({ id: "buttons.unarchive", defaultMessage: "Unarchive" })}
              </Button>
            ) : null}
            {actionAllowed("share") ? (
              <Button
                title={intl.formatMessage({
                  id: "buttons.make_public",
                  defaultMessage: "Make public",
                })}
                leftIcon={<BsShare />}
                onClick={share}
              >
                {intl.formatMessage({ id: "buttons.make_public", defaultMessage: "Make public" })}
              </Button>
            ) : null}
            {actionAllowed("delete") ? (
              <DeleteExportButton exportType={exportType} id={Number(id)} />
            ) : null}
          </ButtonGroup>
        </Flex>

        <Box rounded="md" w="full" border="1px solid #ccc" my="40px">
          <Grid w="full" gap={0} overflow="hidden">
            <GridItem colSpan={5} borderBottom="1px solid #ccc">
              <Box p="14px">
                <Text fontWeight="semibold" fontSize="2xl">
                  {bulkExport?.name}
                </Text>
                <Text fontWeight="normal" fontSize="sm" py="4px">
                  {bulkExport?.created_at.toLocaleDateString()}{" "}
                  {bulkExport?.created_at.toLocaleTimeString()}
                </Text>
              </Box>
            </GridItem>
            <GridItem colSpan={5} borderBottom="1px solid #ccc">
              <Box p="14px">
                <Text fontWeight="bold">
                  <FormattedMessage id="misc.description" defaultMessage="Description" />
                </Text>
                <Text>{bulkExport?.description}</Text>
              </Box>
            </GridItem>
            <GridItem colSpan={5}>
              <Flex h="100%">
                <Box p="14px" borderRight={"1px solid #ccc"}>
                  <Text fontWeight="bold">Status</Text>
                  <ExportStatus status={bulkExport?.status ?? undefined} />{" "}
                  {bulkExport?.status === "submitted" ? <Spinner size="xs" /> : null}
                </Box>
                <Box p="14px" borderRight={"1px solid #ccc"}>
                  <Text fontWeight="bold">
                    <FormattedMessage id="tableheader.error" defaultMessage="Error" />
                  </Text>
                  <Text>{errors.length === 0 ? "-" : errors}</Text>
                </Box>
                <Box p="14px" borderRight={"1px solid #ccc"}>
                  <Text fontWeight="bold">
                    <FormattedMessage
                      id="export.last_executed_datetime"
                      defaultMessage="Last executed"
                    />
                  </Text>
                  <Text>
                    {bulkExport?.updated_at.toLocaleDateString()}{" "}
                    {bulkExport?.updated_at.toLocaleTimeString()}
                  </Text>
                </Box>
                <Box p="14px">
                  <Text fontWeight="bold">
                    <FormattedMessage id="export.created_by" defaultMessage="Created by" />
                  </Text>
                  <ChakraLink href={`mailto:${bulkExport?.created_by.name}`} isExternal>
                    {bulkExport?.created_by.name}
                  </ChakraLink>
                </Box>
              </Flex>
            </GridItem>
          </Grid>
        </Box>

        {bulkExport && exportType === "bulk_file" ? (
          <ExportBulkFileFields
            readonly={!editAllowed}
            value={bulkExport.config as BulkFileConfig}
            onChange={(partialConfig) => updateConfig({ ...bulkExport.config, ...partialConfig })}
            formErrorKeys={formErrorKeys}
          />
        ) : null}

        {bulkExport && exportType === "composite_map" ? (
          <ExportCompositeMapsFields
            readonly={!editAllowed}
            value={bulkExport.config as CompositeMapConfig}
            onChange={(partialConfig) => updateConfig({ ...bulkExport.config, ...partialConfig })}
            formErrorKeys={formErrorKeys}
          />
        ) : null}

        {bulkExport && exportType === "ror" ? (
          <ExportRorFields
            readonly={!editAllowed}
            value={bulkExport.config as RorConfig}
            onChange={(partialConfig) => updateConfig({ ...bulkExport.config, ...partialConfig })}
            formErrorKeys={formErrorKeys}
          />
        ) : null}

        {changeExportError ? (
          <ErrorBox
            errors={[
              intl.formatMessage({
                defaultMessage: "An unknown error occurred updating the export.",
                id: "error.export.unknown",
              }),
            ]}
            mb={4}
          />
        ) : null}

        {bulkExport && Object.keys(bulkExport.files).length > 0 && actionAllowed("download") ? (
          <BulkExportDownload bulkExport={bulkExport} />
        ) : null}

        {actionAllowed("update") ? (
          <Flex alignItems="center" gap={4}>
            <ButtonGroup isAttached variant="outline">
              <Input
                ref={addScenarioInputRef}
                w="8rem"
                size="sm"
                colorScheme="whiteAlpha"
                bgColor="white"
                placeholder={intl.formatMessage({
                  id: "toscenario.placeholder",
                  defaultMessage: "Scenario ID",
                })}
                onKeyUp={(e) => e.key === "Enter" && addScenario()}
                isDisabled={exportUpdating}
              />
              <Button h={8} onClick={addScenario} isDisabled={exportUpdating}>
                {intl.formatMessage({
                  id: "bulkexport.add",
                  defaultMessage: "Add to export",
                })}
              </Button>
            </ButtonGroup>
            <Box bgColor="blue.50" px="4" py="2" rounded="lg">
              <Text color="blue.500" fontWeight="normal">
                <span aria-label="tip" role="img">
                  💡
                </span>
                &nbsp;
                {intl.formatMessage({
                  id: "bulkexport.tip.scenarioscreen.use_the",
                  defaultMessage: "Use the",
                })}
                &nbsp;
                <Link style={{ textDecoration: "underline" }} href="/scenarios">
                  {intl.formatMessage({
                    id: "bulkexport.tip.scenarioscreen.page",
                    defaultMessage: "scenarios page",
                  })}
                </Link>{" "}
                {intl.formatMessage({
                  id: "bulkexport.tip.scenarioscreen.to_search_and_add",
                  defaultMessage: "to search and add scenarios to this export",
                })}
              </Text>
            </Box>
          </Flex>
        ) : null}
        <Flex
          rounded="md"
          w="full"
          border="1px solid #ccc"
          my="40px"
          flexDirection="column"
          justifyItems="flex-start"
        >
          <Flex w="full" borderBottom="1px solid #ccc" alignItems="center" px={4}>
            <Text fontWeight="bold" p="14px">
              <FormattedMessage id="bulkexport.scenarios" defaultMessage="Scenarios" />
            </Text>
            <Flex
              h="12px"
              justifyContent="center"
              alignItems="center"
              color="bij12_1.700"
              fontWeight="semibold"
              bgColor={"#CC614D20"}
              fontSize={"sm"}
              rounded="full"
              p="14px"
              ml="2px"
            >
              {bulkExport?.scenario_ids.length}
            </Flex>
            <Spacer />
            <Text fontStyle="italic" fontSize="80%">
              (
              <NotAllowedIcon />:{" "}
              <FormattedMessage
                id="bulkexport.scenario_not_exportable"
                defaultMessage="this scenario is not exportable"
              />
              )
            </Text>
          </Flex>
          <Grid
            templateColumns="1fr 80px 20px 200px 100px"
            templateRows="repeat(10, 32px)"
            gap={4}
            p={4}
          >
            {scenarioResponses.map((scenarioResponse, idx) =>
              scenarioResponse.isSuccess ? (
                <ExportListItem
                  key={scenarioResponse.data.id}
                  scenario={scenarioResponse.data}
                  removeAllowed={actionAllowed("update")}
                  removeDisabled={exportUpdating}
                  removeScenario={() => removeScenarioId(scenarioResponse.data.id)}
                />
              ) : scenarioResponse.isError ? (
                <EmptyExportListItem
                  key={scenarioIds[idx]}
                  scenarioId={scenarioIds[idx]}
                  removeAllowed={actionAllowed("update")}
                  removeDisabled={exportUpdating}
                  removeScenario={() => removeScenarioId(scenarioIds[idx])}
                />
              ) : (
                <GridItem colSpan={5} key={scenarioIds[idx]}>
                  &nbsp;
                </GridItem>
              )
            )}
            {numPages > 1 && pagination.page === numPages
              ? new Array(pagination.pageSize - scenarioResponses.length).fill(0).map((_, idx) => (
                  <GridItem colSpan={5} key={idx}>
                    &nbsp;
                  </GridItem>
                ))
              : null}
          </Grid>
          {numPages > 1 ? (
            <Center>
              <Pagination
                toPage1={() => pagination.setPage(1)}
                toLastPage={() => pagination.setPage(numPages)}
                toPrevious={pagination.previousPage}
                toNext={pagination.page < numPages ? pagination.nextPage : undefined}
                page={pagination.page}
                numPages={numPages}
                p={4}
              />
            </Center>
          ) : null}
        </Flex>
        {actionAllowed("submit") && canStartLoaded ? (
          <>
            {!canStart ? (
              <Flex align="center" gap={2} mb={2}>
                <MdErrorOutline />
                <Text fontWeight="semibold">
                  <FormattedMessage
                    id="bulkexport.scenarios_cantstart"
                    defaultMessage="Export can not be started as some of the scenarios can not be seen by you, or have statuses that can't be exported."
                  />
                </Text>
              </Flex>
            ) : null}
            <Button
              onClick={submitExport}
              title={intl.formatMessage({
                id: "pages.export_scenarios.submit",
                defaultMessage: "Start export",
              })}
              isDisabled={!canStart}
            >
              <FormattedMessage id="pages.export_scenarios.submit" defaultMessage="Start export" />
            </Button>
          </>
        ) : null}
        {actionAllowed("map") && bulkExport?.rasters?.length ? (
          <BulkExportRastersMap
            size="l"
            bulkExport={bulkExport}
            mapId={`bulkExport-map-${bulkExport.id}`}
            w="full"
            h="2xl"
            flexShrink="0"
            rounded="lg"
            borderRadius="4px"
            bgColor="white"
            position="relative"
            my="4"
          />
        ) : null}
      </Container>
    </Box>
  );
}
